import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { GroupBy } from 'src/app/core/_enums/groupby';
import { SiteMapUrl } from 'src/app/core/_enums/sitemap.url';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { Atelier } from 'src/app/core/_models/atelier';
import { Ceu } from 'src/app/core/_models/ceu';
import { Engin } from 'src/app/core/_models/engin';
import { ProjectType, compare } from 'src/app/core/_models/projectType';
import { Version } from 'src/app/core/_models/version';
import { AuthService } from 'src/app/core/_services/auth.service';
import { EnginAtelierService } from 'src/app/core/_services/engin.atelier.service';
import { ProjectTypeService } from 'src/app/core/_services/projectTypes.service';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { VersionService } from 'src/app/core/_services/versions.service';
import { ResourceListRefreshService } from 'src/app/core/_services/resource.list.refresh.service';
import { UtilsService } from 'src/app/core/_utils/utils.service';
import { validateProjectTypeRequired } from 'src/app/core/_utils/validation';
import { StoreSelect, UpdateResourceAction } from 'src/app/store/actions/resource.actions';
import { AppState } from 'src/app/store/app.state';
import { GlobalConstants } from 'src/constants/global.constants';
import { Energie } from '../../core/_models/energie';
import { EnergieService } from '../../core/_services/energie.service';
import { DialogAddCompositionComponent } from '../shared/dialog-add-composition/dialog-add-composition.component';
import { DialogDuplicateCodeComponent } from '../shared/dialog-duplicate-code/dialog-duplicate-code.component';
import { DialogEditEnginComponent } from '../shared/dialog-edit-engin/dialog-edit-engin.component';
import { DialogOverviewComponent } from '../shared/dialog-overview/dialog-overview.component';

@Component({
  selector: 'app-atelier',
  templateUrl: './atelier.component.html',
  styleUrls: ['./atelier.component.scss']
})
export class AtelierComponent implements OnInit {
  EMPTY_GUID = GlobalConstants.EMPTY_GUID;
  @Input() groupBy!: GroupBy;
  @ViewChild('resourceSection', { static: false }) resourceSection!: ElementRef;

  currentItem: Atelier = {};
  form!: FormGroup;
  families!: any[];
  perimeterId!: string;
  favoriteId?: string;
  projectTypes!: ProjectType[];

  dbVersions: Version[] = [];
  energies: Energie[] = [];
  gnrEnergyId?: string;
  ceuError: boolean = false;
  formSubmitted?: boolean;
  private enginsById: { [id: string]: any } = {};

  constructor(
    private service: EnginAtelierService,
    private activeRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private router: Router,
    private snackbarService: SnackbarService,
    private store: Store<AppState>,
    public dialog: MatDialog,
    private utilsService: UtilsService,
    private versionService: VersionService,
    private authService: AuthService,
    private projectTypeService: ProjectTypeService,
    private translate: TranslateService,
    private energieService: EnergieService,
    private resourceListRefreshService: ResourceListRefreshService
  ) {}

  ngOnInit(): void {
    this.perimeterId = this.authService.roleSubject.value.perimeterId;
    this.activeRoute.params.subscribe((params) => {
      this.form = this.formBuilder.group({
        code: ['', Validators.required],
        displayName: ['', Validators.required],
        owner: [{ value: '', disabled: true }],
        creator: [{ value: '', disabled: true }],
        detailedResource: [{ value: true, disabled: true }],
        familyId: ['', Validators.required],
        description: [''],
        ceuCarburant: this.formBuilder.group({
          ceuCarburantEn: ['', Validators.required],
          ceuCarburantCo: ['', Validators.required]
        }),
        ceuTransfert: this.formBuilder.group({
          ceuTransfertEn: ['', Validators.required],
          ceuTransfertCo: ['', Validators.required]
        }),
        ceuFabrication: this.formBuilder.group({
          ceuFabricationEn: ['', Validators.required],
          ceuFabricationCo: ['', Validators.required]
        }),
        ceuEntretien: this.formBuilder.group({
          ceuEntretienEn: ['', Validators.required],
          ceuEntretienCo: ['', Validators.required]
        }),
        ceu: this.formBuilder.group({
          en: ['', Validators.required],
          co: ['', Validators.required]
        }),
        ceuHeure: this.formBuilder.group({
          en: ['', Validators.required],
          co: ['', Validators.required]
        }),
        roadProject: [],
        earthworkProject: [],
        projectType: [[], validateProjectTypeRequired('validation_project_type_required')],
        versionId: ['']
      });

      this.form.get('ceuCarburant')?.disable();
      this.form.get('ceuTransfert')?.disable();
      this.form.get('ceuFabrication')?.disable();
      this.form.get('ceuEntretien')?.disable();
      this.form.get('ceu')?.disable();
      this.form.get('ceuHeure')?.disable();

      const id = params['id'];
      const resourceId = id === 'new' ? undefined : id;

      this.versionService.getAll().subscribe((versions) => {
        this.dbVersions = versions;
        this.loadLists(resourceId).finally(() => {
          if (resourceId) {
            this.loadAtelier(resourceId);
          } else {
            let obj: Atelier = { detailedResource: true };
            this.initForm(this.form, obj);
          }
        });
      });
    });
  }

  async loadAtelier(resourceId: string) {
    this.service.getAtelier(resourceId).subscribe(
      (item) => {
        this.initForm(this.form, item);
      },
      (error) => {
        this.snackbarService.open(SnackbarTypes.error);
        this.router.navigate([SiteMapUrl.MenuEnginAtelier]);
      }
    );
  }

  async loadLists(resourceId?: string) {
    this.families = !history.state?.families ? await this.service.getFamilies().toPromise() : history.state.families;
    this.projectTypes = !history.state?.projectTypes ? await this.projectTypeService.getAll().toPromise() : history.state.projectTypes;
    this.energies = (await this.energieService.getAllForVehicle().toPromise()) ?? [];
    this.gnrEnergyId = this.energies.find((e) => e.code === 'GNR')?.resourceId;
    await this.loadEnginsIndex();
  }

  private async loadEnginsIndex() {
    const tree = (await this.service.getAll(GroupBy.family).toPromise()) ?? [];
    this.enginsById = {};
    const collect = (nodes: any) => {
      if (!nodes) return;
      if (Array.isArray(nodes)) {
        nodes.forEach(collect);
        return;
      }
      if (nodes.resourceId) this.enginsById[nodes.resourceId] = nodes;
      if (nodes.value) collect(nodes.value);
      if (nodes.engins) collect(nodes.engins);
      if (typeof nodes === 'object') {
        Object.keys(nodes).forEach((k) => {
          const v = (nodes as any)[k];
          if (v && typeof v === 'object' && k !== 'value' && k !== 'engins') collect(v);
        });
      }
    };
    collect(tree);
  }

  initForm(form: FormGroup, currentItem: Atelier) {
    this.currentItem = currentItem;
    if (this.currentItem && this.currentItem.resourceId) {
      this.utilsService.getFavoriteIdForResource(this.currentItem.resourceId).then((res) => {
        this.favoriteId = res ? res.userFavoriteId : '';
      });
    }

    form.reset();

    form.get('ceuCarburant')?.disable();
    form.get('ceuTransfert')?.disable();
    form.get('ceuFabrication')?.disable();
    form.get('ceuEntretien')?.disable();

    this.normalizeEngins();

    form.patchValue(currentItem);

    if (!this.formCrls.versionId.value) {
      this.form.patchValue({ versionId: this.dbVersions[0]?.versionId });
    }

    this.formCrls?.ceu?.valueChanges.subscribe(() => {
      this.onChangeValuesCeu();
    });

    if (this.currentItem?.engins?.length) {
      this.refreshCEU();
    } else {
      this.onChangeValuesCeu();
    }
  }

  onChangeValuesCeu() {
    this.formCrls?.ceuHeure?.get('en')?.setValue(this.calculCeuHeure(this.formCrls?.ceu?.get('en')?.value));
    this.formCrls?.ceuHeure?.get('co')?.setValue(this.calculCeuHeure(this.formCrls?.ceu?.get('co')?.value));
  }

  onVersionChange(_value: string) {
    this.refreshCEU();
  }

  onEnginEnergyChange(engin: Engin, value: string) {
    engin.energyId = value;
    engin.energyName = this.energies.find((e) => e.resourceId === value)?.displayName;
    this.refreshCEU();
  }

  private isBlankId(value?: string): boolean {
    return !value || value === GlobalConstants.EMPTY_GUID;
  }

  private normalizeEngins() {
    if (!this.currentItem?.engins) return;
    this.currentItem.engins.forEach((engin) => {
      if (this.isBlankId(engin.resourceId)) engin.resourceId = engin.childResource;
      if (this.isBlankId(engin.vehicleId)) engin.vehicleId = engin.resourceId;
      const canonical = engin.resourceId ? this.enginsById[engin.resourceId] : undefined;
      if (canonical) {
        engin.ruade = !!canonical.ruade;
        engin.ownerId = engin.ownerId ?? canonical.ownerId;
        engin.familyCode = engin.familyCode ?? canonical.familyCode;
        engin.justified = engin.justified ?? canonical.justified;
      }
      if (engin.ruade) {
        if (this.isBlankId(engin.energyId)) engin.energyId = this.gnrEnergyId;
      } else {
        engin.energyId = !this.isBlankId(canonical?.energyId) ? canonical.energyId : GlobalConstants.EMPTY_GUID;
      }
      if (engin.energyId && engin.energyId !== GlobalConstants.EMPTY_GUID) {
        engin.energyName = this.energies.find((e) => e.resourceId === engin.energyId)?.displayName ?? engin.energyName;
      } else {
        engin.energyName = undefined;
      }
    });
  }

  private buildComputePayload(): Atelier {
    const engins = (this.currentItem.engins ?? []).map((e) => {
      const resourceId = this.isBlankId(e.resourceId) ? e.childResource : e.resourceId;
      const vehicleId = this.isBlankId(e.vehicleId) ? resourceId : e.vehicleId;
      return {
        resourceId,
        vehicleId,
        resourceCompositionId: e.resourceCompositionId,
        quantity: e.quantity,
        rank: e.rank,
        energyId: e.energyId && e.energyId !== GlobalConstants.EMPTY_GUID ? e.energyId : undefined,
        ruade: e.ruade
      };
    }) as Engin[];

    return {
      ...this.currentItem,
      engins
    };
  }

  refreshCEU() {
    this.ceuError = false;
    this.markUnavailableEngins([], []);
    const versionId = this.formCrls.versionId.value || this.dbVersions?.[0]?.versionId;
    if (!versionId) return;

    const hasEngins = (this.currentItem?.engins?.length ?? 0) > 0;
    if (!hasEngins) {
      this.form.patchValue({ ceu: { en: 0, co: 0 } });
      this.onChangeValuesCeu();
      return;
    }

    this.service.computeCEU(this.buildComputePayload(), versionId).subscribe({
      next: (response) => {
        const unavailableVehicles = response?.unavailableVehicles ?? [];
        const unavailableEnergyIds = response?.unavailableEnergyIds ?? [];
        this.markUnavailableEngins(unavailableVehicles, unavailableEnergyIds);
        const hasUnavailable = unavailableVehicles.length > 0 || unavailableEnergyIds.length > 0;
        if (hasUnavailable) {
          this.ceuError = true;
          this.form.patchValue({ ceu: { en: 0, co: 0 } });
          this.onChangeValuesCeu();
          return;
        }
        const en = response?.ceu?.find((c) => c.displayName === 'EN')?.valueDay ?? 0;
        const co = response?.ceu?.find((c) => c.displayName === 'CO')?.valueDay ?? 0;
        this.form.patchValue({ ceu: { en, co } });
        this.onChangeValuesCeu();
      },
      error: () => {
        this.ceuError = true;
        this.form.patchValue({ ceu: { en: 0, co: 0 } });
        this.onChangeValuesCeu();
      }
    });
  }

  private markUnavailableEngins(unavailableVehicles: string[], unavailableEnergyIds: string[]) {
    (this.currentItem?.engins ?? []).forEach((engin) => {
      engin.vehicleUnavailable = !!engin.vehicleId && unavailableVehicles.includes(engin.vehicleId);
      engin.energyUnavailable = !!engin.energyId && engin.energyId !== GlobalConstants.EMPTY_GUID && unavailableEnergyIds.includes(engin.energyId);
    });
  }

  isFormDisable() {
    var isInCreation = !this.currentItem || !this.currentItem.resourceId;
    return !isInCreation && !this.currentItem.canUpdate;
  }

  get formCrls() {
    return this.form.controls;
  }

  onDplicate(resourceId: string) {
    this.dialog
      .open(DialogDuplicateCodeComponent, { data: { code: this.currentItem.code, maxLength: 10, duplicate: (code: string) => this.service.duplicateAtelier(resourceId, code) } })
      .afterClosed()
      .subscribe((res) => {
        if (res !== undefined) this.majStore(SnackbarTypes.duplicate);
      });
  }

  clickSave(currentItem: Atelier) {
    this.formSubmitted = true;
    if (this.form.invalid) {
      if (this.formCrls.projectType.invalid) this.scrollToBottom();
      return;
    }

    currentItem = Object.assign(currentItem, this.form.value);
    currentItem.detailedResource = true;
    currentItem.ceu = this.mapFormToCeu(this.form.value);
    currentItem.engins = (currentItem.engins ?? []).map((engin) => ({
      ...engin,
      energyId: engin.energyId && engin.energyId !== GlobalConstants.EMPTY_GUID ? engin.energyId : undefined
    }));

    const isUpdate = !!this.currentItem?.resourceId;
    const snackbarType = isUpdate ? SnackbarTypes.update : SnackbarTypes.add;
    currentItem.versionId = this.currentItem?.ruade ? this.formCrls.versionId.value : undefined;
    delete currentItem.energyId;

    const previousCanUpdate = this.currentItem?.canUpdate;
    this.onUpdateOrSaveAch(currentItem).subscribe(
      (data: Atelier) => {
        this.currentItem = data;
        if (this.currentItem.canUpdate === undefined) this.currentItem.canUpdate = isUpdate ? (previousCanUpdate ?? true) : true;
        this.normalizeEngins();
        this.majStore(snackbarType);

        const navState = {
          perimeterId: this.perimeterId,
          resourceId: data.resourceId,
          families: this.families,
          projectTypes: this.projectTypes
        };

        this.router.navigate([SiteMapUrl.MenuEnginAtelier, 'atelier', data.resourceId], { state: navState });
      },
      (error) => {
        this.snackbarService.openError(error);
      }
    );
  }

  mapFormToCeu(formValue: any): Ceu {
    return {
      en: formValue.ceu?.en ?? 0,
      co: formValue.ceu?.co ?? 0,
      ceuEngin: {
        ca: {
          energie: formValue.ceuCarburant?.ceuCarburantEn ?? 0,
          co2: formValue.ceuCarburant?.ceuCarburantCo ?? 0
        },
        tr: {
          energie: formValue.ceuTransfert?.ceuTransfertEn ?? 0,
          co2: formValue.ceuTransfert?.ceuTransfertCo ?? 0
        },
        fa: {
          energie: formValue.ceuFabrication?.ceuFabricationEn ?? 0,
          co2: formValue.ceuFabrication?.ceuFabricationCo ?? 0
        },
        en: {
          energie: formValue.ceuEntretien?.ceuEntretienEn ?? 0,
          co2: formValue.ceuEntretien?.ceuEntretienCo ?? 0
        }
      }
    };
  }

  onUpdateOrSaveAch(currentItem: Atelier) {
    return this.currentItem?.resourceId ? this.service.updateAtelier(currentItem) : this.service.addAtelier(currentItem);
  }

  majStore(snackbarMsgType: string) {
    this.store.select(StoreSelect.RESOURCES).subscribe((data) => {
      this.store.dispatch(new UpdateResourceAction(data));
    });

    this.service.getAll(GroupBy.family).subscribe(
      (arr) => {
        if (arr) {
          this.store.dispatch(new UpdateResourceAction(arr));
          this.snackbarService.open(snackbarMsgType);
        }
      },
      (error) => {
        this.snackbarService.open(SnackbarTypes.error);
      }
    );

    this.resourceListRefreshService.triggerRefresh();
  }

  onDeleteDialog(resourceId: string): void {
    const dialogRef = this.dialog.open(DialogOverviewComponent, {
      data: { title: this.translate.instant('confirm_delete_engin') }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleteAtelier(resourceId);
      }
    });
  }

  deleteAtelier(resourceId: string) {
    if (resourceId) {
      this.service.deleteAtelier(resourceId).subscribe({
        next: () => {
          this.majStore(SnackbarTypes.delete);
          this.router.navigate([SiteMapUrl.MenuEnginAtelier]);
        },
        error: (error) => {
          this.snackbarService.open(SnackbarTypes.error);
          console.error(error);
        }
      });
    }
  }

  addEngin() {
    this.service.getOnlyEngins().subscribe((data) => {
      this.service.getOnlyEngins(GroupBy.favoriteDialog).subscribe((favoriteData) => {
        const dialogRef = this.dialog.open(DialogAddCompositionComponent, {
          data: {
            fullTree: data,
            favoriteData: favoriteData
          }
        });

        dialogRef.afterClosed().subscribe((data) => {
          if (!data) return;
          if (!this.currentItem.engins) this.currentItem.engins = [];

          data.forEach((el: any) => {
            const isRuade = !!el?.ruade;
            let energyId: string | undefined;
            if (isRuade) {
              energyId = this.gnrEnergyId;
            } else {
              energyId = el?.energyId && el.energyId !== GlobalConstants.EMPTY_GUID ? el.energyId : GlobalConstants.EMPTY_GUID;
            }
            const energyName = energyId && energyId !== GlobalConstants.EMPTY_GUID ? this.energies.find((e) => e.resourceId === energyId)?.displayName : undefined;

            this.currentItem.engins?.push({
              resourceId: el?.resourceId,
              vehicleId: el?.resourceId,
              code: el?.code,
              displayName: el?.displayName,
              quantity: 1,
              rank: this.currentItem?.engins?.length,
              ceu: el?.ceus,
              ruade: el?.ruade,
              ownerId: el?.ownerId,
              familyCode: el?.familyCode,
              justified: el?.justified,
              fileName: el?.fileName,
              fCeuFileName: el?.fCeuFileName,
              energyId,
              energyName
            });
          });

          this.refreshCEU();
        });
      });
    });
  }

  editEngin(item: Engin) {
    const dialogRef = this.dialog.open(DialogEditEnginComponent, {
      data: {
        quantity: item.quantity,
        displayName: item.displayName,
        ceu: item?.ceu,
        isAtelier: true
      }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        item.quantity = data?.quantity;
        this.refreshCEU();
      }
    });
  }

  deleteEnginFromList(position: number, engins: Engin[]) {
    if (!this.currentItem?.engins) return;
    this.currentItem.engins.splice(position, 1);
    engins.forEach((e, i) => (e.rank = i));
    this.refreshCEU();
  }

  enginMove(engins: Engin[], index: number, new_index: number) {
    engins.splice(new_index, 0, engins.splice(index, 1)[0]);
    const rank = engins[new_index].rank;
    engins[new_index].rank = engins[index].rank;
    engins[index].rank = rank;
    return engins;
  }

  getEnginEnergyLabel(engin: Engin): string {
    if (!engin?.energyId || engin.energyId === GlobalConstants.EMPTY_GUID) return this.translate.instant('common_other_energy');
    return engin.energyName ?? this.energies.find((e) => e.resourceId === engin.energyId)?.displayName ?? this.translate.instant('common_other_energy');
  }

  calculCeuHeure(val: any) {
    let dailyUsageTime = this.currentItem.dailyUsageTime;
    if (!dailyUsageTime) dailyUsageTime = 7;
    if (val) return val / dailyUsageTime;
    return 0;
  }

  addToFavorite(resourceId: string) {
    this.utilsService.addToFavorite(resourceId).then((res) => {
      this.favoriteId = res;
    });
  }

  removeFromFavorite() {
    if (this.favoriteId) {
      this.utilsService.removeFromFavorite(this.favoriteId).then(() => {
        this.favoriteId = '';
      });
    }
  }

  private scrollToBottom(): void {
    if (this.resourceSection?.nativeElement) {
      this.resourceSection.nativeElement.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }

  compareProjectTypes(pt1: ProjectType, pt2: ProjectType) {
    return compare(pt1, pt2);
  }
}
